import { useState } from 'react';
import type { FC, MouseEventHandler } from 'react';
import { Flex } from '@chakra-ui/react';
import { Link, type LinkProps } from 'react-router-dom';
import { classNames } from '../../utils/tailwind/classNames';

type LinkWrapperProps = LinkProps & { showLoader?: boolean; isDisabled?: boolean };

export const SoftLink: FC<LinkWrapperProps> = ({
    to,
    showLoader,
    children,
    isDisabled,
    className,
    onClick,
    ...rest
}) => {
    const [loading, setLoading] = useState(false);

    if (typeof to === 'function') {
        return null;
    }

    const location = typeof to === 'string' ? { pathname: to } : to;

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (e): void => {
        if (isDisabled) {
            e.preventDefault();
        }

        if (onClick) {
            onClick(e);
        }

        if (!loading) {
            setLoading(true);
        }
    };

    return (
        <Link
            to={to}
            {...rest}
            onClick={handleClick}
            className={classNames(className, {
                relative: showLoader && loading,
                'pointer-events-none': isDisabled,
            })}
            role="link"
            aria-disabled={isDisabled}
            data-link-type="soft-link"
        >
            {children}
            {showLoader && loading ? (
                <Flex
                    data-testid={`link-loading-indicator-${location.pathname}`}
                    position="absolute"
                    top={0}
                    right={0}
                    left={0}
                    bottom={0}
                    margin="0 !important"
                    backgroundColor="white"
                    opacity="0.7"
                    zIndex="2"
                />
            ) : null}
        </Link>
    );
};

SoftLink.defaultProps = {
    showLoader: false,
    isDisabled: false,
};
